import { useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import { ProtectedRoute } from "@/lib/auth/protected-route"
import { AppLayout } from "@/components/layout/app-layout"
import { BankNote03 } from "@untitledui/icons"
import { Roles } from "@/lib/team/roles"
import { useOrganization } from "@/lib/organization/organization-context"
import { useEstimates } from "@/lib/api/use-estimates"
import { teamsApi } from "@/lib/api/teams"
import { ApiError } from "@/lib/api/client"
import { useTeamMembers } from "@/lib/api/use-teams"
import type { ApiTeam, TeamSettingsMetadata } from "@/lib/api/types"
import { PayoutsView } from "@/components/payouts/payouts-view"

// Manager-facing payout reconciliation — the admin counterpart to the
// advisor wallet. Body-shop-scoped: the collision center funds the advisor
// rewards program at the dealership routing estimates to it, so the body
// shop manager reviews and settles each cycle. The ledger stays keyed to
// the dealership (the program team) so advisor wallets keep reading their
// own team's records; only the operator moved. Non-bodyshop contexts get a
// friendly pointer instead.
//
// The program dealership is resolved through GET /v1/teams/{id}/bodyshop/
// dealerships (the shop's own scoped endpoint — full ApiTeam[] including
// metadata), NOT via GET /v1/teams/{id}, which 403s for non-members.

export default function PayoutsPage() {
  const { activeBodyShopId, activeTeamKind } = useOrganization()
  const bodyshopId = activeTeamKind === "bodyshop" ? activeBodyShopId : null

  const { data: connectedTeams } = useQuery({
    queryKey: ["payouts", "connected-dealerships", bodyshopId],
    queryFn: async (): Promise<ApiTeam[]> => {
      try {
        return await teamsApi.listConnectedDealerships(Number(bodyshopId))
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return []
        throw err
      }
    },
    enabled: !!bodyshopId,
    staleTime: 5 * 60 * 1000,
  })

  // The program store: the connected dealership with goals & rewards
  // configured — that's whose advisors are owed money.
  const team = useMemo(
    () =>
      (connectedTeams ?? []).find(
        (t) => !!(t.metadata as TeamSettingsMetadata | undefined)?.goalsRewards,
      ) ?? connectedTeams?.[0],
    [connectedTeams],
  )
  const teamId = team?.id ?? null
  const { data: members } = useTeamMembers(teamId)
  const { data: estimates } = useEstimates(teamId)

  return (
    <ProtectedRoute allowedRoles={[Roles.GeneralManager]}>
      <AppLayout>
        {bodyshopId ? (
          <PayoutsView
            team={team}
            teamId={teamId}
            estimates={estimates ?? []}
            members={members}
          />
        ) : (
          <div className="flex-1 p-4 md:p-8 max-w-[1400px] mx-auto w-full">
            <div className="animate-element rounded-2xl border border-zinc-200/60 bg-white shadow-sm px-6 py-16 text-center">
              <div className="size-12 rounded-2xl bg-zinc-100 flex items-center justify-center mx-auto mb-4">
                <BankNote03 className="size-6 text-zinc-400" aria-hidden />
              </div>
              <h1 className="text-lg font-semibold text-zinc-900">Your collision center settles payouts</h1>
              <p className="text-sm text-zinc-400 mt-1 max-w-md mx-auto">
                Advisor rewards are funded and paid by the body shop your estimates route to.
                Earnings still land in each advisor&apos;s wallet — the shop&apos;s manager
                reviews and settles them every cycle.
              </p>
            </div>
          </div>
        )}
      </AppLayout>
    </ProtectedRoute>
  )
}
